import { MouseEvent, useCallback } from 'react';

import { ApiSlideshowData } from 'api/types/ApiSlideshowData';
import { Carousel, useCarouselState } from 'components/Carousel';
import { FilestackImage } from 'components/FilestackImage/FilestackImage';
import { VideoThumbnail } from 'components/VideoThumbnail/VideoThumbnail';
import { useIsClient } from 'hooks/useIsClient';
import { useViewportBreakpointValue } from 'hooks/viewport/useViewportBreakpointValue';

import { SlideshowItem } from './Slideshow.styled';
import { SlideshowAccessibleButton } from './SlideshowAccessibleButton';
import { SlideshowNavigation } from './SlideshowNavigation';

type Props = {
  carouselId: string;
  items: ApiSlideshowData[];
  fullWidthLayout?: boolean;
  onClickIndex?: (data: { itemIndex: number }) => void;
  'data-qa-id'?: string;
};

export function Slideshow({
  carouselId,
  items,
  fullWidthLayout,
  onClickIndex,
  'data-qa-id': qaId = 'carousel',
}: Props) {
  const carouselState = useCarouselState({
    slideCount: items.length,
    responsiveSlidesPerPage: {
      xs: 1,
      sm: 3,
      md: 3,
      lg: 3,
    },
    capSlidesPerPageToSlideCount: true,
  });

  const size = useViewportBreakpointValue({
    xs: 240,
    sm: 240,
    md: 240,
    lg: fullWidthLayout ? 360 : 240,
  });

  const clickAccessibleButton = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      onClickIndex?.({ itemIndex: 0 });
    },
    [onClickIndex],
  );

  const isClient = useIsClient();

  return (
    <div style={{ position: 'relative' }}>
      {onClickIndex && (
        <SlideshowAccessibleButton
          aria-label={getText('Open gallery')}
          title="Open image gallery"
          $navShown={carouselState.pageCount > 1}
          onClick={clickAccessibleButton}
        />
      )}

      <Carousel
        id={carouselId}
        state={carouselState}
        data-qa-id={qaId}
        height={size}
        navigation={
          isClient && (
            <SlideshowNavigation
              carouselState={carouselState}
              fullWidth={false}
            />
          )
        }
        slides={items?.map((item, index) => (
          <SlideshowItem
            key={item.handle || item.videoUrl}
            $clickable={Boolean(onClickIndex)}
            data-qa-id={`${qaId}-image-container`}
            aria-label={getText('Open gallery')}
            tabIndex={-1}
            onClick={(e: MouseEvent) => {
              e.preventDefault();
              onClickIndex?.({ itemIndex: index });
            }}
          >
            {item.fileId && (
              <FilestackImage
                data-qa-id={`${qaId}-image`}
                data={item}
                key={item.handle}
                alt={item.altText || ''}
                width={size}
                height={size}
                objectFit="cover"
                style={{ borderRadius: '28px' }}
              />
            )}

            {item.type === 'VIDEO' && item.videoUrl && (
              <VideoThumbnail
                src={{ type: 'video-thumbnail', videoUrl: item.videoUrl }}
                qaId={`${qaId}-image`}
                size={{ type: 'expand' }}
                playIcon={{ size: 'large' }}
              />
            )}
          </SlideshowItem>
        ))}
      />
    </div>
  );
}
